import React, { useRef, useState } from "react";
import { useList } from "@/lib/hooks";
import api from "@/lib/api";
import { uploadFileChunked } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FileText, UploadSimple, Trash, DownloadSimple, FilePdf, Image as ImageIcon, Camera } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DOC_TYPES = ["rate_confirmation", "bol", "pod", "invoice", "other"];
const LABELS = { rate_confirmation: "Rate Confirmation", bol: "BOL", pod: "POD", invoice: "Invoice", other: "Other" };

function fileIcon(ct) {
  if (String(ct).includes("pdf")) return <FilePdf size={18} className="text-red-500" weight="duotone" />;
  if (String(ct).startsWith("image")) return <ImageIcon size={18} className="text-blue-500" weight="duotone" />;
  return <FileText size={18} className="text-slate-400" weight="duotone" />;
}

export default function LoadDocuments({ loadId, canUpload = true, canDelete = false, allowCamera = false }) {
  const { data: docs = [], isLoading, refetch } = useList(["documents", loadId], `/loads/${loadId}/documents`);
  const [docType, setDocType] = useState("pod");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef();
  const cameraRef = useRef();

  const onFile = async (e, forceType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File too large (max 25MB)"); return; }
    setUploading(true); setProgress(0);
    try {
      await uploadFileChunked(file, loadId, forceType || docType, setProgress);
      toast.success("Document uploaded");
      refetch();
    } catch (err) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false); setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); refetch(); }
    catch (e) { toast.error("Could not delete"); }
  };

  return (
    <div className="space-y-3" data-testid="load-documents">
      {canUpload && (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger className="w-full sm:w-48 bg-white border-slate-300" data-testid="doc-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{LABELS[t]}</SelectItem>)}
            </SelectContent>
          </Select>
          <input ref={fileRef} type="file" accept=".pdf,image/*" className="hidden" onChange={onFile} data-testid="doc-file-input" />
          <Button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="bg-blue-600 text-white hover:bg-blue-700 font-semibold"
            data-testid="upload-doc-btn"
          >
            <UploadSimple size={18} weight="bold" className="mr-1.5" />
            {uploading ? `Uploading… ${progress}%` : "Upload Document"}
          </Button>
          {allowCamera && (
            <>
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onFile(e, "pod")} data-testid="doc-camera-input" />
              <Button
                type="button"
                variant="outline"
                onClick={() => cameraRef.current?.click()}
                disabled={uploading}
                className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 font-semibold"
                data-testid="capture-photo-btn"
              >
                <Camera size={18} weight="bold" className="mr-1.5" />Take POD Photo
              </Button>
            </>
          )}
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-slate-400 py-4">Loading documents…</p>
      ) : docs.length === 0 ? (
        <p className="text-sm text-slate-400 py-6 text-center">No documents attached yet.</p>
      ) : (
        <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50" data-testid={`doc-row-${d.id}`}>
              {fileIcon(d.content_type)}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800 truncate">{d.original_filename}</p>
                <p className="text-xs text-slate-400">
                  <span className="uppercase tracking-wide font-semibold text-blue-600">{LABELS[d.doc_type] || d.doc_type}</span>
                  {" · "}{(d.size / 1024).toFixed(0)} KB · {d.uploaded_by_name || "—"}
                </p>
              </div>
              <a
                href={`${API}/documents/${d.id}/download`}
                target="_blank"
                rel="noreferrer"
                className="p-1.5 text-slate-400 hover:text-blue-600"
                data-testid={`download-doc-${d.id}`}
                title="View / download"
              >
                <DownloadSimple size={18} />
              </a>
              {canDelete && (
                <button onClick={() => remove(d.id)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`delete-doc-${d.id}`} title="Delete">
                  <Trash size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
